import { Alert, Avatar, Badge, Box, Chip, Grid, IconButton, Snackbar, Stack, Tooltip } from "@mui/material";
import Iconify from "components/iconify";
import MKTypography from "components/MKTypography";
import PropTypes from "prop-types";
import CartoonCard from "./CartoonCard";
import borders from "assets/theme/base/borders";
import { useState } from "react";

// 🎨 Rank color map
const rankColors = {
  1: "#FFD700", // Gold
  2: "#C0C0C0", // Silver
  3: "#cd7f32", // Bronze
  default: "#e0e0e0", // Light grey
};

export default function CartoonCardAvatar({
  name,
  role,
  trimRole = false,
  description,
  socialLinks,
  color = "white",
  secondaryColor = "white",
  backgroundColor = "white",
  tilt = true,
  baseTileAngle = 0,
  endTiltAngle = 3,
  avatar,
  sx,
  onPlay,
  onShare,
  onVote,
  votes = 0,
  rank,
  isSmall = false,
  disabledActions = {},
  score,
}) {
  const hasActions = onPlay || onShare || onVote;
  const rankColor = rankColors[rank] || rankColors.default;
  const { vote: isVoteDisabled, play: isPlayDisabled, share: isShareDisabled } = disabledActions;
  const [showCopySnackbar, setShowCopySnackbar] = useState(false);
  const textToCopy = role && role.startsWith("by ") ? role.slice(3) : role;
  const copyToClipboard = () => {
    navigator.clipboard.writeText(textToCopy);
    setShowCopySnackbar(true);
  };
  

  const getScoreStyle = (score, isSmall) => {
    const styles = [
      { bg: "#fef2f2", text: "#991b1b" },
      { bg: "#fee2e2", text: "#991b1b" },
      { bg: "#fecaca", text: "#7f1d1d" },
      { bg: "#fca5a5", text: "#7f1d1d" },
      { bg: "#fb7185", text: "#7f1d1d" },
      { bg: "#f43f5e", text: "#fff" },
      { bg: "#e11d48", text: "#fff" },
      { bg: "#be123c", text: "#fff" },
      { bg: "#9f1239", text: "#fff" },
      { bg: "#b91c1c", text: "#fff" },
    ];

    const index = Math.min(Math.floor(score / 100), 9);
    const { bg, text } = styles[index];

    return {
      backgroundColor: bg,
      color: text,
      fontWeight: "bold",
      fontSize: isSmall ? "0.8rem" : "1rem",
      px: 1.5,
      py: 0.5,
      borderRadius: "16px",
      border: "2px solid black",
      boxShadow: "3px 3px 0 rgba(0,0,0,0.3)",
      transform: "rotate(-5deg)",
      transition: "transform 0.2s ease-in-out",
      zIndex: 2,
      "&:hover": {
        transform: "rotate(-5deg) scale(1.1)",
      },
    };
  };

  return (
    <>
      <CartoonCard
        color={color}
        secondaryColor={secondaryColor}
        tilt={tilt}
        baseTileAngle={baseTileAngle}
        endTiltAngle={endTiltAngle}
        sx={{
          p: isSmall ? 2 : 3,
          // height: "100%",
          // display: "flex",
          // flexDirection: "column",
          // justifyContent: "space-between",
          ...sx,
        }}
      >
        {/* 🧱 Top Content */}
        <Stack spacing={2} sx={{ flexGrow: 1 }}>
          {/* Avatar & Rank */}
          <Box sx={{ position: "relative", display: "flex", justifyContent: "center", mb: 1 }}>
            {rank !== undefined && (
              <Chip
                label={`#${rank}`}
                sx={{
                  position: "absolute",
                  top: -5,
                  left: -5,
                  backgroundColor: rankColor,
                  fontWeight: "bold",
                  fontSize: "1rem",
                  border: "2px solid black",
                  boxShadow: "3px 3px 0 rgba(0,0,0,0.4)",
                  zIndex: 1,
                }}
              />
            )}
            {score !== undefined && (
              <Box sx={{ position: "absolute", bottom: -12, right: -8 }}>
                <Box sx={getScoreStyle(0, isSmall)}>Score: {score}</Box>
              </Box>
            )}
            <Box
              sx={{
                width: "100%",
                backgroundColor,
                borderRadius: borders.borderRadius.lg,
                border: "4px solid black",
                boxShadow: "6px 6px 0px rgba(0,0,0,0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={avatar}
                alt={name}
                sx={{
                  width: isSmall ? "80%" : "100%",
                  borderRadius: isSmall ? "50%" : "16px",
                  objectFit: "cover",
                  backgroundColor: "transparent",
                }}
              />
              {/* Score Badge */}
            </Box>
          </Box>

          {/* Name */}
          <MKTypography
            variant={isSmall ? "h5" : "h3"}
            color="black"
            fontWeight="bold"
            sx={{
              textShadow: "2px 2px white",
              letterSpacing: 1,
              textAlign: "center",
            }}
          >
            {name}
          </MKTypography>

          {/* Role */}
          {role && (
            <Tooltip
              title={trimRole && role.length > 10 ? "Click to copy" : ""}
              arrow
              disableHoverListener={!trimRole || role.length <= 10}
            >
              <MKTypography
                variant="body1"
                color="black"
                sx={{
                  fontWeight: "bold",
                  textShadow: "1px 1px white",
                  letterSpacing: 1,
                  textAlign: "center",
                  fontSize: isSmall ? "0.85rem" : "1rem",
                  cursor: trimRole && role.length > 10 ? "pointer" : "default",
                }}
                onClick={trimRole && role.length > 10 ? copyToClipboard : undefined}
              >
                {trimRole && role.length > 10 ? `${role.slice(0, 10)}...` : role}
              </MKTypography>
            </Tooltip>
          )}
          {/* Description */}
          <MKTypography
            variant="body2"
            color="black"
            sx={{
              textAlign: "center",
              lineHeight: 1.5,
              textShadow: "1px 1px white",
              fontSize: isSmall ? "0.75rem" : "0.875rem",
              whiteSpace: "pre-line",
              // maxHeight: isSmall ? "60px" : "80px",
              overflowY: "auto",
              px: 1,
            }}
          >
            {description}
          </MKTypography>

          {/* Socials */}
          {socialLinks?.length > 0 && (
            <Grid container spacing={2} justifyContent="center" mt={1}>
              {socialLinks.map((link, index) => (
                <Grid item key={index}>
                  <IconButton
                    component="a"
                    href={link.href}
                    target="_blank"
                    sx={iconStyle(isSmall)}
                  >
                    <Iconify
                      icon={link.icon}
                      width={isSmall ? 20 : 24}
                      height={isSmall ? 20 : 24}
                    />
                  </IconButton>
                </Grid>
              ))}
            </Grid>
          )}
        </Stack>

        {/* 🎯 Action Buttons at Bottom */}
        {hasActions && (
          <Stack direction="row" justifyContent="center" spacing={2} mt={2}>
            {onPlay && (
              <Tooltip title="Play Fart">
                <IconButton
                  disabled={isPlayDisabled}
                  onClick={onPlay}
                  sx={iconStyle(isSmall, "#8b5cf6", "#6d28d9")}
                >
                  <Iconify
                    icon="mdi:play-circle"
                    width={isSmall ? 22 : 28}
                    height={isSmall ? 22 : 28}
                  />
                </IconButton>
              </Tooltip>
            )}
            {onShare && (
              <Tooltip title="Share">
                <IconButton
                  disabled={isShareDisabled}
                  onClick={onShare}
                  sx={iconStyle(isSmall, "#0ea5e9", "#0284c7")}
                >
                  <Iconify
                    icon="mdi:share-variant"
                    width={isSmall ? 22 : 26}
                    height={isSmall ? 22 : 26}
                  />
                </IconButton>
              </Tooltip>
            )}
            {onVote && (
              <Tooltip title="Vote">
                <Badge
                  badgeContent={votes}
                  color="secondary"
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontWeight: "bold",
                      fontSize: isSmall ? "0.6rem" : "0.75rem",
                      backgroundColor: "#22c55e",
                      color: "white",
                      border: "2px solid black",
                      boxShadow: "2px 2px 0 rgba(0,0,0,0.3)",
                    },
                  }}
                >
                  <IconButton
                    disabled={isVoteDisabled}
                    onClick={onVote}
                    sx={iconStyle(isSmall, "#22c55e", "#15803d")}
                  >
                    <Iconify
                      icon="mdi:thumb-up"
                      width={isSmall ? 24 : 28}
                      height={isSmall ? 24 : 28}
                    />
                  </IconButton>
                </Badge>
              </Tooltip>
            )}
          </Stack>
        )}
      </CartoonCard>
      <Snackbar
        open={showCopySnackbar}
        autoHideDuration={2000}
        onClose={() => setShowCopySnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowCopySnackbar(false)}
          // severity="success"
          sx={{
            backgroundColor: "#22c55e",
            color: "white",
            border: "2px solid black",
            fontWeight: "bold",
            borderRadius: borders.borderRadius.lg,
          }}
        >
          Copied: {textToCopy}
        </Alert>
      </Snackbar>
    </>
  );
}

const iconStyle = (isSmall, color, hoverColor) => ({
  backgroundColor: color,
  borderRadius: "50%",
  width: isSmall ? 42 : 52,
  height: isSmall ? 42 : 52,
  border: "3px solid black",
  boxShadow: "4px 4px 0px rgba(0,0,0,0.4)",
  color: "white",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  transition:
    "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, background-color 0.2s ease-in-out",
  "&:hover": {
    transform: "scale(1.08)",
    backgroundColor: hoverColor || color,
    boxShadow: "6px 6px 0px rgba(0,0,0,0.4)",
  },
  "&:active": {
    transform: "scale(0.95)",
    boxShadow: "3px 3px 0px rgba(0,0,0,0.4)",
  },
});

// ✅ Prop Types
CartoonCardAvatar.propTypes = {
  name: PropTypes.string.isRequired,
  role: PropTypes.string.isRequired,
  trimRole: PropTypes.bool,
  description: PropTypes.string,
  socialLinks: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    })
  ),
  color: PropTypes.string,
  secondaryColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  tilt: PropTypes.bool,
  baseTileAngle: PropTypes.number,
  endTiltAngle: PropTypes.number,
  avatar: PropTypes.string.isRequired,
  onPlay: PropTypes.func,
  onShare: PropTypes.func,
  onVote: PropTypes.func,
  votes: PropTypes.number,
  rank: PropTypes.number,
  isSmall: PropTypes.bool,
  score: PropTypes.number, // Add score prop type
};
